import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { MILESTONES_SEED } from '@common/data';
import type { Milestone, MilestoneDraft, MilestoneScope } from '@common/types';

import { CurrentUserService } from './current-user.service';

const LIST_LATENCY_MS = 240;
const SAVE_LATENCY_MS = 300;

@Injectable({ providedIn: 'root' })
export class MilestonesService {
  private readonly currentUser = inject(CurrentUserService);
  private readonly milestones = signal<ReadonlyArray<Milestone>>(MILESTONES_SEED);
  private seq = 0;

  readonly all = this.milestones.asReadonly();

  /**
   * Visibility follows the tag vocabulary rather than a second one: a private milestone is visible
   * only to its owner, everything else is visible to the organisation.
   */
  private isVisible(milestone: Milestone): boolean {
    return milestone.visibility !== 'private' || milestone.ownerId === this.currentUser.id();
  }

  forScope(scope: MilestoneScope, scopeId: string | null): Observable<Milestone[]> {
    const rows = this.milestones()
      .filter((milestone) => milestone.scope === scope)
      .filter((milestone) => (scopeId ? milestone.scopeId === scopeId : true))
      .filter((milestone) => this.isVisible(milestone))
      .sort((a, b) => (a.dateIso < b.dateIso ? 1 : -1));
    return of(rows).pipe(delay(LIST_LATENCY_MS));
  }

  create(draft: MilestoneDraft): Observable<Milestone> {
    const milestone: Milestone = {
      ...draft,
      id: `ms-${Date.now().toString(36)}-${++this.seq}`,
      ownerId: draft.visibility === 'private' ? this.currentUser.id() : null,
    };
    this.milestones.update((list) => [milestone, ...list]);
    return of(milestone).pipe(delay(SAVE_LATENCY_MS));
  }

  update(id: string, draft: MilestoneDraft): Observable<Milestone | undefined> {
    this.milestones.update((list) =>
      list.map((milestone) => (milestone.id === id ? { ...milestone, ...draft } : milestone)),
    );
    return of(this.milestones().find((milestone) => milestone.id === id)).pipe(
      delay(SAVE_LATENCY_MS),
    );
  }

  remove(id: string): Observable<void> {
    this.milestones.update((list) => list.filter((milestone) => milestone.id !== id));
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }
}
